import { getBackendUrl } from '../lib/api';
import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Loader2, CheckCircle, ClipboardList, Send, AlertCircle } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import ToolsInput from '../components/qualification/ToolsInput';

const loadSavedProgress = (token) => {
  if (!token) return null;
  try {
    const raw = localStorage.getItem(`qualif_progress_${token}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.answers && typeof parsed.answers === 'object') {
      return {
        answers: parsed.answers,
        currentSection: typeof parsed.currentSection === 'number' ? parsed.currentSection : 0,
      };
    }
  } catch (_) { /* ignore corrupt storage */ }
  return null;
};

const QualificationTokenPage = () => {
  const { token } = useParams();
  const storageKey = `qualif_progress_${token}`;
  const saved = loadSavedProgress(token);

  const [tokenData, setTokenData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);
  const [currentSection, setCurrentSection] = useState(saved?.currentSection || 0);
  const [answers, setAnswers] = useState(saved?.answers || {});
  const [toolsMastered, setToolsMastered] = useState(
    Array.isArray(saved?.tools_mastered) ? saved.tools_mastered : []
  );
  const restoredToastShown = useRef(false);

  useEffect(() => {
    fetchTokenData();
    // Inform the candidate that their previous progress was restored (once)
    if (saved && !restoredToastShown.current) {
      const count = Object.keys(saved.answers).filter(k => (saved.answers[k] || '').toString().trim()).length;
      if (count > 0) {
        toast.success(`Progression restaurée (${count} réponse${count > 1 ? 's' : ''})`);
        restoredToastShown.current = true;
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Auto-save progress whenever answers or section change
  useEffect(() => {
    if (!token || submitted) return;
    try {
      localStorage.setItem(
        storageKey,
        JSON.stringify({
          answers,
          currentSection,
          tools_mastered: toolsMastered,
          savedAt: Date.now(),
        })
      );
    } catch (_) { /* quota or disabled, ignore */ }
  }, [answers, currentSection, toolsMastered, token, submitted, storageKey]);

  const fetchTokenData = async () => {
    try {
      const response = await fetch(`${getBackendUrl()}/api/qualification/token/${token}`);
      if (!response.ok) {
        setError(response.status === 404 ? 'invalid' : 'error');
        setLoading(false);
        return;
      }
      const data = await response.json();
      setTokenData(data);
    } catch (err) {
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({ ...prev, [questionId]: value }));
  };

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const form = tokenData?.form;
      const allQuestions = form?.sections
        ? form.sections.flatMap(s => s.questions || [])
        : form?.questions || [];

      const formattedAnswers = allQuestions.map(q => ({
        question_id: q.id,
        question: q.question || q.text,
        answer: answers[q.id] || '',
        category: q.category || ''
      }));

      const response = await fetch(`${getBackendUrl()}/api/qualification/token/${token}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formattedAnswers)
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitted(true);
        // Clean up saved progress for this token
        try { localStorage.removeItem(storageKey); } catch (_) { /* ignore */ }
        toast.success('Merci ! Votre évaluation a été soumise.');
      } else {
        const err = await response.json();
        const msg = typeof err.detail === 'string' ? err.detail : 
                    Array.isArray(err.detail) ? err.detail.map(e => e.msg || e).join(', ') :
                    'Erreur lors de la soumission';
        toast.error(msg);
      }
    } catch (err) {
      toast.error('Erreur de connexion');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0F2744] to-[#152F57] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-white" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0F2744] to-[#152F57] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {error === 'invalid' ? 'Lien invalide ou expiré' : 'Erreur'}
          </h2>
          <p className="text-gray-500">
            {error === 'invalid'
              ? 'Ce lien d\'évaluation n\'est plus valide. Veuillez contacter l\'équipe Talent Partner.'
              : 'Une erreur est survenue. Veuillez réessayer.'}
          </p>
        </Card>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0F2744] to-[#152F57] flex items-center justify-center p-4">
        <Card className="max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="h-8 w-8 text-green-600" />
          </div>
          <h2 className="text-xl font-semibold mb-2">Merci !</h2>
          <p className="text-gray-500 mb-4">
            Votre évaluation a été soumise avec succès. Notre équipe va analyser vos réponses et reviendra vers vous rapidement.
          </p>
          <p className="text-sm text-gray-400">
            Vous pouvez fermer cette page.
          </p>
        </Card>
      </div>
    );
  }

  const candidate = tokenData?.candidate || {};
  const form = tokenData?.form || {};
  const allQuestions = form?.sections
    ? form.sections.flatMap((s, si) => (s.questions || []).map(q => ({ ...q, section: s.title || `Section ${si + 1}`, category: q.category || s.title })))
    : (form?.questions || []).map(q => ({ ...q, section: 'Questions', category: q.category || '' }));

  // Group by section
  const sections = [];
  const sectionMap = {};
  allQuestions.forEach(q => {
    const sKey = q.section;
    if (!sectionMap[sKey]) {
      sectionMap[sKey] = { title: sKey, questions: [] };
      sections.push(sectionMap[sKey]);
    }
    sectionMap[sKey].questions.push(q);
  });

  const currentSectionData = sections[currentSection] || { title: '', questions: [] };
  const totalSections = sections.length;
  const progress = totalSections > 0 ? ((currentSection + 1) / totalSections) * 100 : 0;
  const answeredCount = Object.keys(answers).filter(k => answers[k]?.trim()).length;
  const totalQuestions = allQuestions.length;

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0F2744] to-[#152F57]">
      <SeoHead
        pageTitle="Évaluation candidat — Talent Partner"
        pageDescription="Formulaire de qualification privé (accès par lien sécurisé)."
        noindex={true}
      />
      {/* Header */}
      <div className="bg-white/10 backdrop-blur border-b border-white/10 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-white font-semibold text-lg">Talent Partner</h1>
            <p className="text-white/60 text-sm">Évaluation de {candidate.first_name} {candidate.last_name}</p>
          </div>
          <div className="text-right">
            <p className="text-white/60 text-xs">{answeredCount}/{totalQuestions} réponses</p>
            <p className="text-white text-sm font-medium">Section {currentSection + 1}/{totalSections}</p>
            {answeredCount > 0 && (
              <p className="text-white/50 text-[10px] mt-0.5" data-testid="auto-save-indicator">
                Progression sauvegardée
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Progress */}
      <div className="max-w-2xl mx-auto px-4 mt-4">
        <Progress value={progress} className="h-2" />
      </div>

      {/* Form content */}
      <div className="max-w-2xl mx-auto px-4 py-6">
        <Card className="p-6">
          <div className="flex items-center gap-2 mb-6">
            <ClipboardList className="h-5 w-5 text-[#4C8F6E]" />
            <h2 className="text-lg font-semibold">{currentSectionData.title}</h2>
          </div>

          <div className="space-y-6">
            {currentSectionData.questions.map((q, idx) => (
              <div key={q.id || idx} data-testid={`question-${q.id || idx}`}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {idx + 1}. {q.question || q.text}
                </label>
                {q.type === 'choice' || q.options ? (
                  <div className="space-y-2">
                    {(q.options || []).map((opt, oi) => {
                      const optValue = typeof opt === 'string' ? opt : opt.value || opt.label;
                      const optLabel = typeof opt === 'string' ? opt : opt.label || opt.value;
                      return (
                        <label
                          key={oi}
                          className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                            answers[q.id] === optValue ? 'border-[#4C8F6E] bg-green-50' : 'border-gray-200 hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name={q.id}
                            value={optValue}
                            checked={answers[q.id] === optValue}
                            onChange={() => handleAnswerChange(q.id, optValue)}
                            className="sr-only"
                          />
                          <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                            answers[q.id] === optValue ? 'border-[#4C8F6E]' : 'border-gray-300'
                          }`}>
                            {answers[q.id] === optValue && <div className="w-2 h-2 rounded-full bg-[#4C8F6E]" />}
                          </div>
                          <span className="text-sm">{optLabel}</span>
                        </label>
                      );
                    })}
                  </div>
                ) : (
                  <Textarea
                    value={answers[q.id] || ''}
                    onChange={(e) => handleAnswerChange(q.id, e.target.value)}
                    placeholder="Votre réponse..."
                    rows={3}
                    className="w-full"
                  />
                )}
              </div>
            ))}
          </div>

          {/* Outils maîtrisés — affiché uniquement sur la dernière section */}
          {currentSection === totalSections - 1 && (
            <div className="mt-8 pt-6 border-t border-gray-200">
              <label className="block text-sm font-semibold text-gray-800 mb-2">
                Outils maîtrisés
              </label>
              <p className="text-xs text-gray-500 mb-3">
                Sélectionnez ou ajoutez tous les outils, plateformes et logiciels que vous utilisez réellement au quotidien. Plus la liste est précise, plus votre profil ressort en sourcing.
              </p>
              <ToolsInput
                value={toolsMastered}
                onChange={setToolsMastered}
                profileType={candidate?.primary_category || candidate?.job_category || 'default'}
              />
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Précédent
            </Button>

            {currentSection < totalSections - 1 ? (
              <Button onClick={() => setCurrentSection(currentSection + 1)}>
                Suivant
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                disabled={submitting || answeredCount === 0}
                className="bg-[#4C8F6E] hover:bg-[#3d7a5c] text-white"
                data-testid="submit-qualification-btn"
              >
                {submitting ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                ) : (
                  <Send className="h-4 w-4 mr-2" />
                )}
                Envoyer mes réponses
              </Button>
            )}
          </div>
        </Card>
      </div>
    </div>
  );
};

export default QualificationTokenPage;
